import React, { useRef } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { API_URL as SERVER } from "../config";

const SAUNA_IMAGES = gql`
  query SaunaImages($saunaId: Int!) {
    allSaunaImages(condition: { saunaId: $saunaId }, orderBy: DISPLAY_ORDER_ASC) {
      nodes { id url displayOrder }
    }
  }
`;

const ADD_IMAGE = gql`
  mutation AddSaunaImage($saunaId: Int!, $url: String!, $displayOrder: Int!) {
    createSaunaImage(input: { saunaImage: { saunaId: $saunaId, url: $url, displayOrder: $displayOrder } }) {
      saunaImage { id }
    }
  }
`;

const DELETE_IMAGE = gql`
  mutation DeleteSaunaImage($id: Int!) {
    deleteSaunaImageById(input: { id: $id }) {
      saunaImage { id }
    }
  }
`;

interface Props {
  saunaId: number;
}

export default function SaunaImageManager({ saunaId }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { data, loading } = useQuery(SAUNA_IMAGES, { variables: { saunaId } });
  const [addImage, { loading: adding }] = useMutation(ADD_IMAGE, { refetchQueries: ["SaunaImages"] });
  const [deleteImage] = useMutation(DELETE_IMAGE, { refetchQueries: ["SaunaImages"] });

  const images: any[] = data?.allSaunaImages?.nodes ?? [];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const token = sessionStorage.getItem("jwt");
    let currentImages = [...images];
    const errors: string[] = [];

    for (const file of files) {
      const form = new FormData();
      form.append("image", file);

      try {
        const res = await fetch(`${SERVER}/api/uploads/sauna-image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!res.ok) throw new Error(await res.text());
        const { url } = await res.json();
        const nextOrder = currentImages.length > 0 ? Math.max(...currentImages.map((i: any) => i.displayOrder)) + 1 : 0;
        await addImage({ variables: { saunaId, url, displayOrder: nextOrder } });
        currentImages = [...currentImages, { displayOrder: nextOrder }];
      } catch (err: any) {
        errors.push(`${file.name}: ${err.message}`);
      }
    }

    if (fileRef.current) fileRef.current.value = "";
    if (errors.length > 0) alert(`Some uploads failed:\n${errors.join("\n")}`);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 15 }}>Photos</h4>
        <label style={uploadBtnStyle}>
          {adding ? "Uploading..." : "+ Add photo"}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={adding}
          />
        </label>
      </div>

      {loading && <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading photos...</p>}

      {images.length === 0 && !loading && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>No photos yet. Add at least one to attract renters.</p>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {images.map((img: any) => (
          <div key={img.id} style={{ position: "relative" }}>
            <img
              src={`${SERVER}${img.url}`}
              alt=""
              style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }}
            />
            <button
              onClick={() => deleteImage({ variables: { id: img.id } })}
              style={deleteBtnStyle}
              title="Remove photo"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const uploadBtnStyle: React.CSSProperties = {
  fontSize: 13, padding: "6px 14px", background: "#2563EB", color: "#fff",
  borderRadius: 6, cursor: "pointer", border: "none",
};

const deleteBtnStyle: React.CSSProperties = {
  position: "absolute", top: 4, right: 4, width: 22, height: 22,
  background: "rgba(0,0,0,0.6)", color: "#fff", border: "none",
  borderRadius: "50%", cursor: "pointer", fontSize: 16, lineHeight: "1",
  display: "flex", alignItems: "center", justifyContent: "center",
};
